import { lazy, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { safeNext } from "../core/account/safeNext";
import { LoadingScreen, MessageScreen } from "../components/StatusScreen";
import { en } from "../i18n/en";
import { takeReturnPath } from "../platform/web/returnPath";
import { useAccount } from "./account";

// Everything that's only for signed-in people (portfolio, providers, the app shell) loads in
// its own chunk, so signed-out visitors only download the sign-in pages (12 FR-89).
const SignedIn = lazy(() => import("./SignedIn"));

/**
 * The layout route around every protected route (12 FR-79), so a new screen is protected by
 * default. Signed-out visitors go to sign in, and come back here afterwards (FR-80, FR-81).
 */
export function RequireAccount() {
  const { state, retry } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = state.status === "signed-in";

  // Back from Google: go to the page they were on before signing in.
  useEffect(() => {
    if (!signedIn) return;
    const next = takeReturnPath();
    if (next) navigate(safeNext(next), { replace: true });
  }, [signedIn, navigate]);

  switch (state.status) {
    case "checking":
      return <LoadingScreen />;
    case "unreachable":
      return (
        <MessageScreen
          title={en.account.unreachable.title}
          body={en.account.unreachable.body}
          action={{ label: en.account.unreachable.retry, onClick: retry }}
        />
      );
    case "signed-out": {
      const here = location.pathname + location.search + location.hash;
      const to = state.byUser ? "/sign-in" : `/sign-in?next=${encodeURIComponent(here)}`;
      return <Navigate to={to} replace />;
    }
    case "signed-in":
      // A new account (or none) means a fresh portfolio and settings in memory (11 FR-60).
      return <SignedIn key={state.account.id} account={state.account} />;
  }
}
